import { useMemo, useState } from "react";
import { GroupChannel } from "@sendbird/chat/groupChannel";
import { usePopupStore } from "@/lib/hooks/conversations/use-popup-store";
import { useGlobalChats } from "@/lib/hooks/conversations/use-global-chats";
import { useTeenChannelCategories } from "@/lib/hooks/conversations/use-teen-channel-categories";
import { ConversationAccordionSection } from "./conversation-accordion-section";

interface ConversationAccordionListingProps {
  currentChannelUrl: string | null;
  setCurrentChannelUrl: (url: string) => void;
  channels: GroupChannel[];
  channelsLoading: boolean;
  shouldEverShowCheckinBadge?: boolean;
  showSupervisorSection?: boolean;
}

export function ConversationAccordionListing({
  currentChannelUrl,
  setCurrentChannelUrl,
  channels,
  channelsLoading,
  shouldEverShowCheckinBadge = false,
  showSupervisorSection = false,
}: ConversationAccordionListingProps) {
  const { setChatPopupOpen } = usePopupStore();
  const { setSupervisorUnreadMessage } = useGlobalChats();

  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({
    needsResponse: true,
    online: false,
    lowEngagement: false,
    newTeens: false,
    allTeens: false,
    supervisor: false,
  });

  const categories = useTeenChannelCategories(channels);

  const unreadCounts = useMemo(
    () => ({
      needsResponse: sumUnread(categories.needsResponse),
      online: sumUnread(categories.online),
      lowEngagement: sumUnread(categories.lowEngagement),
      newTeens: sumUnread(categories.newTeens),
      allTeens: sumUnread(categories.allTeens),
      supervisor: sumUnread(categories.supervisor),
    }),
    [categories],
  );

  const toggleSection = (sectionKey: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const handleSupervisorChannelSelect = (_channelUrl: string) => {
    setChatPopupOpen(true);
    setSupervisorUnreadMessage(false);
  };

  const loading = channelsLoading || categories.loading;

  return (
    <div>
      <ConversationAccordionSection
        title="Needs response"
        count={categories.needsResponse.length}
        channels={categories.needsResponse}
        isExpanded={expandedSections.needsResponse}
        onToggle={() => toggleSection("needsResponse")}
        currentChannelUrl={currentChannelUrl}
        setCurrentChannelUrl={setCurrentChannelUrl}
        shouldEverShowCheckinBadge={shouldEverShowCheckinBadge}
        loading={loading}
        unreadCount={unreadCounts.needsResponse}
      />

      <ConversationAccordionSection
        title="Online"
        count={categories.online.length}
        channels={categories.online}
        isExpanded={expandedSections.online}
        onToggle={() => toggleSection("online")}
        currentChannelUrl={currentChannelUrl}
        setCurrentChannelUrl={setCurrentChannelUrl}
        shouldEverShowCheckinBadge={shouldEverShowCheckinBadge}
        loading={loading}
        unreadCount={unreadCounts.online}
      />

      <ConversationAccordionSection
        title="Low engagement"
        count={categories.lowEngagement.length}
        channels={categories.lowEngagement}
        isExpanded={expandedSections.lowEngagement}
        onToggle={() => toggleSection("lowEngagement")}
        currentChannelUrl={currentChannelUrl}
        setCurrentChannelUrl={setCurrentChannelUrl}
        shouldEverShowCheckinBadge={shouldEverShowCheckinBadge}
        loading={loading}
        unreadCount={unreadCounts.lowEngagement}
      />

      <ConversationAccordionSection
        title="New teens"
        count={categories.newTeens.length}
        channels={categories.newTeens}
        isExpanded={expandedSections.newTeens}
        onToggle={() => toggleSection("newTeens")}
        currentChannelUrl={currentChannelUrl}
        setCurrentChannelUrl={setCurrentChannelUrl}
        shouldEverShowCheckinBadge={shouldEverShowCheckinBadge}
        loading={loading}
        unreadCount={unreadCounts.newTeens}
      />

      <ConversationAccordionSection
        title="All teens"
        count={categories.allTeens.length}
        channels={categories.allTeens}
        isExpanded={expandedSections.allTeens}
        onToggle={() => toggleSection("allTeens")}
        currentChannelUrl={currentChannelUrl}
        setCurrentChannelUrl={setCurrentChannelUrl}
        shouldEverShowCheckinBadge={shouldEverShowCheckinBadge}
        loading={loading}
        unreadCount={unreadCounts.allTeens}
      />

      {showSupervisorSection && (
        <ConversationAccordionSection
          title="Supervisor"
          count={categories.supervisor.length}
          channels={categories.supervisor}
          isExpanded={expandedSections.supervisor}
          onToggle={() => toggleSection("supervisor")}
          currentChannelUrl={currentChannelUrl}
          setCurrentChannelUrl={setCurrentChannelUrl}
          shouldEverShowCheckinBadge={shouldEverShowCheckinBadge}
          loading={loading}
          isSupervisorSection={true}
          onSupervisorChannelSelect={handleSupervisorChannelSelect}
          unreadCount={unreadCounts.supervisor}
        />
      )}
    </div>
  );
}

function sumUnread(channels: GroupChannel[]): number {
  return channels.reduce((total, c) => total + c.unreadMessageCount, 0);
}
